import React, {useMemo, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Snackbar, IconButton as MIconButton} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import {RootState} from 'appredux/reducers';
import {
  shouldShowOverviewWidget,
  widgetComponentMap,
} from 'screens/Dashboard/Overview/widgets';
import {
  rdwAddWidget,
  rdwRemoveWidget,
  rdwSetWidgetLayout,
} from 'appredux/actions';

import './CustomizeOverviewPanel.scss';

/* ----------------------------------------------------------- */
type WidgetKey = keyof typeof widgetComponentMap;
type FilterMode = 'all' | 'added' | 'available';

/* ----------------------------------------------------------- */
export default function CustomizeOverviewPanel(): React.ReactElement {
  /* Redux ---------------------------------------------------- */
  const {layout} = useSelector((s: RootState) => s.reduxDashboardWidgets);
  const user = useSelector((s: RootState) => s.user);
  const dispatch = useDispatch();

  /* Local state ---------------------------------------------- */
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');

  /* which widgets are already on the dashboard? -------------- */
  const present = useMemo(
    () => new Set<WidgetKey>(layout.map((l) => l.i as WidgetKey)),
    [layout],
  );

  const allKeys = useMemo(
    () =>
      (Object.keys(widgetComponentMap) as WidgetKey[]).filter((id) =>
        shouldShowOverviewWidget(id, user),
      ),
    [user],
  );

  /* filtered + searched keys --------------------------------- */
  const filteredKeys = useMemo(() => {
    let keys = allKeys;

    // apply feature toggle filter
    if (filter === 'added') {
      keys = keys.filter((k) => present.has(k));
    } else if (filter === 'available') {
      keys = keys.filter((k) => !present.has(k));
    }

    // apply search filter
    if (search.trim()) {
      const term = search.toLowerCase();
      keys = keys.filter((k) => {
        const niceName = k.replace(/([A-Z])/g, ' $1').toLowerCase();
        return niceName.includes(term) || k.toLowerCase().includes(term);
      });
    }

    return keys;
  }, [allKeys, present, search, filter]);

  /* Snackbar ------------------------------------------------- */
  const [snack, setSnack] = useState({open: false, msg: ''});
  const showSnack = (msg: string) => setSnack({open: true, msg});

  /* CTA handlers -------------------------------------------- */
  const addWidget = (id: WidgetKey) => {
    dispatch(rdwAddWidget(id as any));
    showSnack('Widget added');
  };
  const removeWidget = (id: WidgetKey) => {
    dispatch(rdwRemoveWidget(id as any));
    showSnack('Widget removed');
  };

  /* Counts for the toggle pills ------------------------------ */
  const addedCount = allKeys.filter((k) => present.has(k)).length;
  const availableCount = allKeys.length - addedCount;

  /* Empty-state (all widgets already present) ---------------- */
  const allAdded = allKeys.every((k) => present.has(k));

  /* ---------------------------------------------------------- */
  return (
    <section className="cso-body">
      <h3 className="cso-section">Widgets</h3>
      <p className="cso-copy">
        Click <strong>add</strong> or <strong>remove</strong> widgets below. You
        can still reorder them directly on the overview screen.
      </p>

      {/* Search bar */}
      <div className="cso-search">
        <input
          type="text"
          placeholder="Search widgets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="cso-search-input"
        />
        {search && (
          <button
            className="cso-search-clear"
            onClick={() => setSearch('')}
            type="button"
            aria-label="Clear search"
          >
            &times;
          </button>
        )}
      </div>

      {/* Feature toggle filter */}
      <div className="cso-filter-bar">
        <button
          type="button"
          className={`cso-filter-pill ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({allKeys.length})
        </button>
        <button
          type="button"
          className={`cso-filter-pill ${filter === 'added' ? 'active' : ''}`}
          onClick={() => setFilter('added')}
        >
          Added ({addedCount})
        </button>
        <button
          type="button"
          className={`cso-filter-pill ${
            filter === 'available' ? 'active' : ''
          }`}
          onClick={() => setFilter('available')}
        >
          Available ({availableCount})
        </button>
      </div>

      {allAdded && filter === 'all' && !search ? (
        /* Empty-state ---------------------------------------- */
        <div className="cso-empty">
          <div className="glyph" />
          <p>All available widgets are already on your overview.</p>
          <button
            className="btn-manage"
            onClick={() => dispatch(rdwSetWidgetLayout(layout))}
          >
            Manage layout
          </button>
        </div>
      ) : filteredKeys.length === 0 ? (
        /* No results ----------------------------------------- */
        <div className="cso-empty">
          <p>No widgets match your search.</p>
        </div>
      ) : (
        /* Widget cards -------------------------------------- */
        <div className="cso-list">
          {filteredKeys.map((id) => {
            const isAdded = present.has(id);
            const niceName = id
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (c) => c.toUpperCase());

            const WidgetComponent = widgetComponentMap[id];

            return (
              <article key={id} className={`cso-card cso-card--${id}`}>
                {/* Title + widget preview */}
                <div className="card-main">
                  <header className="card-title">{niceName}</header>
                  <div className="card-preview">
                    <div className="card-preview-inner">
                      <WidgetComponent />
                    </div>
                  </div>
                </div>

                {/* CTA */}
                {isAdded ? (
                  <button
                    className="btn-remove"
                    onClick={() => removeWidget(id)}
                  >
                    Remove
                  </button>
                ) : (
                  <button className="btn-add" onClick={() => addWidget(id)}>
                    + Add
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* MUI Snackbar (top-center by default) */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        message={snack.msg}
        onClose={() => setSnack({...snack, open: false})}
        action={
          <MIconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setSnack({...snack, open: false})}
          >
            <CloseIcon fontSize="small" />
          </MIconButton>
        }
      />
    </section>
  );
}
